/*
 * Zahnheld responsive link
 * ---------------------------------------------------------------------------
 * Some documents exist twice: a 16:9 deck for desktop and a 9:16 deck for
 * phones (the study presentations, for example). The link that opens them often
 * lives in rich text, which only carries href, title and target, so there is no
 * room for a second URL in an attribute. The second URL therefore travels in the
 * fragment of the first one:
 *
 *   https://cdn.shopify.com/.../Studie.pdf?v=1#mobile=Studie_Mobile.pdf?v=2
 *
 * The part after `#mobile=` is resolved relative to the desktop URL, so it is
 * normally just the file name next to it. On a phone viewport the link points
 * at that file; everywhere else the fragment is stripped and the desktop file
 * opens. With JavaScript off the desktop file opens, fragment and all, which a
 * browser simply ignores.
 *
 * Three decisions worth knowing:
 *
 *   - The href itself is rewritten instead of hijacking the click, so long-press
 *     "open in new tab", copy link and middle-click all get the right file.
 *   - Links rendered later (drawers, re-rendered sections) are fixed in the
 *     capture phase of the interaction that opens them, before the browser
 *     reads the href.
 *   - The mobile file must resolve to the same http(s) origin and the same
 *     folder as the desktop file. The fragment is editor content, and an editor
 *     typo should never be able to send a customer to another host or store.
 */
(function () {
  'use strict';

  var MARKER = '#mobile=';
  var SOURCE_ATTR = 'data-zh-responsive-href';
  var SELECTOR = 'a[href*="' + MARKER + '"], a[' + SOURCE_ATTR + ']';
  var FALLBACK_QUERY = 'screen and (max-width: 699px)';

  function mobileQuery() {
    var vars = window.themeVariables;
    var query = vars && vars.breakpoints && vars.breakpoints['sm-max'];
    return window.matchMedia(query || FALLBACK_QUERY);
  }

  // Returns the URL the link should carry, or null when `source` is not a
  // responsive link at all.
  function resolve(source, isMobile) {
    var at = source.indexOf(MARKER);
    if (at === -1) return null;

    var desktop = source.slice(0, at);
    if (!isMobile) return desktop;

    try {
      var base = new URL(desktop, window.location.href);
      var mobile = new URL(decodeURIComponent(source.slice(at + MARKER.length)), base);
      // cdn.shopify.com is shared by every store, so the origin alone proves
      // nothing: the mobile file has to sit in the desktop file's folder.
      var folder = base.pathname.slice(0, base.pathname.lastIndexOf('/') + 1);
      var safe =
        /^https?:$/.test(base.protocol) &&
        mobile.origin === base.origin &&
        mobile.pathname.indexOf(folder) === 0;
      return safe ? mobile.href : desktop;
    } catch (error) {
      return desktop;
    }
  }

  function apply(link, isMobile) {
    var source = link.getAttribute(SOURCE_ATTR);
    if (source === null) {
      source = link.getAttribute('href') || '';
      if (source.indexOf(MARKER) === -1) return;
      link.setAttribute(SOURCE_ATTR, source);
    }

    var target = resolve(source, isMobile);
    if (target !== null && link.getAttribute('href') !== target) {
      link.setAttribute('href', target);
    }
  }

  var query = mobileQuery();

  function applyAll() {
    var links = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < links.length; i++) apply(links[i], query.matches);
  }

  function onInteraction(event) {
    var origin = event.target;
    var link = origin && origin.closest ? origin.closest(SELECTOR) : null;
    if (link) apply(link, query.matches);
  }

  applyAll();

  if (query.addEventListener) {
    query.addEventListener('change', applyAll);
  } else if (query.addListener) {
    query.addListener(applyAll);
  }

  // pointerdown runs before a long-press preview, focusin before the status bar
  // shows the target; Enter on a focused link fires `click`.
  ['pointerdown', 'focusin', 'click', 'auxclick', 'contextmenu'].forEach(function (type) {
    document.addEventListener(type, onInteraction, true);
  });
})();
